'use client'

import { useState } from 'react'

const expectations = [
  'We review your application within 48 hours',
  'Approved clinicians receive a personal access code by email',
  'Sign up and run your first patient interview in under 10 minutes',
  '5 free interviews, no card required',
]

const specialtyOptions = [
  { value: '', label: 'Select your specialty' },
  { value: 'orthopaedics', label: 'Orthopaedics' },
  { value: 'rheumatology', label: 'Rheumatology' },
  { value: 'sleep-medicine', label: 'Sleep Medicine' },
  { value: 'respiratory', label: 'Respiratory' },
  { value: 'gastroenterology', label: 'Gastroenterology' },
  { value: 'neurology', label: 'Neurology' },
  { value: 'psychiatry', label: 'Psychiatry' },
  { value: 'physiotherapy', label: 'Physiotherapy' },
  { value: 'other', label: 'Other' },
]

const patientsPerWeekOptions = [
  { value: '', label: 'Select volume' },
  { value: '1-5', label: '1-5' },
  { value: '6-15', label: '6-15' },
  { value: '16-30', label: '16-30' },
  { value: '30+', label: '30+' },
]

const sourceOptions = [
  { value: '', label: 'Select an option' },
  { value: 'colleague', label: 'Colleague' },
  { value: 'search', label: 'Search' },
  { value: 'conference', label: 'Conference' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'oruk', label: 'ORUK' },
  { value: 'other', label: 'Other' },
]

export function Contact() {
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    specialty: '',
    practiceType: '',
    patientsPerWeek: '',
    clinicalPartner: false,
    source: '',
    message: '',
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setError('')

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          _honey: (document.getElementById('_honey') as HTMLInputElement)?.value,
        }),
      })

      if (!res.ok) {
        const data = await res.json().catch(() => null)
        throw new Error(data?.error || 'Something went wrong. Please try again.')
      }

      setSubmitted(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }))
  }

  return (
    <section id="contact" className="section-inner">
      <div className="overline-label">Get started</div>
      <h2 className="section-h2" style={{ marginBottom: 8 }}>Get in <em>touch</em></h2>
      <p className="section-sub">Tell us about your practice. We review applications within 48 hours and send your access details by email.</p>

      <div className="access-form-layout">
        {/* Left: Form */}
        <div>
          {submitted ? (
            <div className="form-card" style={{ textAlign: 'center', padding: '56px 40px' }}>
              <div style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: 'var(--bg-teal-tint)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
              }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="var(--brand-primary)" strokeWidth="2.5" strokeLinecap="round" style={{ width: 28, height: 28 }}>
                  <path d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 style={{
                fontFamily: 'var(--font-headline)',
                fontSize: 22,
                fontWeight: 400,
                letterSpacing: '-0.02em',
                color: 'var(--text-primary)',
                marginBottom: 8,
              }}>
                Thank you for your application
              </h3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.65 }}>
                We review applications within 48 hours. You&apos;ll receive your access details by email.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="form-card">
              {/* Honeypot */}
              <input
                type="text"
                id="_honey"
                name="_honey"
                tabIndex={-1}
                autoComplete="off"
                style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
              />

              <div className="form-row">
                <label htmlFor="name">Full name <span style={{ color: 'var(--error)' }}>*</span></label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                />
              </div>

              <div className="form-row">
                <label htmlFor="email">Work email <span style={{ color: 'var(--error)' }}>*</span></label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>

              <div className="form-row">
                <label htmlFor="specialty">Specialty <span style={{ color: 'var(--error)' }}>*</span></label>
                <select
                  id="specialty"
                  name="specialty"
                  required
                  value={formData.specialty}
                  onChange={handleChange}
                >
                  {specialtyOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <label>Practice type <span style={{ color: 'var(--error)' }}>*</span></label>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {['Private practice', 'NHS', 'Both'].map((option) => {
                    const selected = formData.practiceType === option
                    return (
                      <label
                        key={option}
                        onClick={() => setFormData(prev => ({ ...prev, practiceType: option }))}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 8,
                          cursor: 'pointer',
                          padding: '10px 16px',
                          border: `1px solid ${selected ? 'var(--brand-primary)' : 'var(--border-card)'}`,
                          borderRadius: 8,
                          fontSize: 14,
                          fontFamily: 'var(--font-body)',
                          color: 'var(--text-primary)',
                          background: selected ? 'rgba(3,161,167,0.04)' : 'var(--bg-card)',
                          transition: 'border-color 0.15s, background 0.15s',
                        }}
                      >
                        <span style={{
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          border: `2px solid ${selected ? 'var(--brand-primary)' : '#d1d5db'}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          transition: 'border-color 0.15s',
                        }}>
                          {selected && (
                            <span style={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              background: 'var(--brand-primary)',
                            }} />
                          )}
                        </span>
                        {option}
                      </label>
                    )
                  })}
                </div>
              </div>

              <div className="form-row">
                <label htmlFor="patientsPerWeek">Approximate new patients per week <span style={{ color: 'var(--error)' }}>*</span></label>
                <select
                  id="patientsPerWeek"
                  name="patientsPerWeek"
                  required
                  value={formData.patientsPerWeek}
                  onChange={handleChange}
                >
                  {patientsPerWeekOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <label className="form-checkbox">
                  <input
                    type="checkbox"
                    name="clinicalPartner"
                    checked={!!formData.clinicalPartner}
                    onChange={handleChange}
                  />
                  I&apos;m interested in the Clinical Partner programme
                </label>
              </div>

              <div className="form-row">
                <label htmlFor="source">How did you hear about us?</label>
                <select
                  id="source"
                  name="source"
                  value={formData.source}
                  onChange={handleChange}
                >
                  {sourceOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <label htmlFor="message">Brief message</label>
                <textarea
                  id="message"
                  name="message"
                  rows={3}
                  value={formData.message}
                  onChange={handleChange}
                />
              </div>

              {error && (
                <p style={{ fontSize: 13, color: 'var(--error)', textAlign: 'center', marginBottom: 16 }}>{error}</p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="btn-primary"
                style={{ width: '100%', textAlign: 'center', opacity: submitting ? 0.6 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }}
              >
                {submitting ? 'Sending...' : 'Get in touch'}
              </button>

              <p style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', marginTop: 12 }}>
                We&apos;ll only use your details to respond to your request.
              </p>
            </form>
          )}
        </div>

        {/* Right: Sidebar */}
        <div>
          <div className="sidebar-card">
            <h4>What to expect</h4>
            {expectations.map((item, i) => (
              <div key={i} className="sidebar-step">
                <div className="sidebar-step-num">{i + 1}</div>
                <div className="sidebar-step-text">{item}</div>
              </div>
            ))}
          </div>
          <div className="sidebar-card" style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="var(--brand-primary)" strokeWidth="1.5" style={{ width: 20, height: 20, flexShrink: 0 }}>
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>Security documentation available on request</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="var(--brand-primary)" strokeWidth="1.5" style={{ width: 20, height: 20, flexShrink: 0 }}>
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
              </svg>
              <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>Start in weeks, not months</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Contact
